/**
 * Main application layout wrapper.
 *
 * Provides the consistent page structure:
 * - Classification banners (top/bottom, if CAPCO enabled)
 * - Navigation bar
 * - Growl/toast notification area
 * - Main content area (React Router outlet)
 *
 * Mirrors the legacy AngularJS index.html layout structure.
 */
import { useEffect } from 'react';
import { Outlet, useLocation } from 'react-router';
import { ClassificationBanner } from '@brew/ui';
import { useAppInfo } from '@/app/useAppInfo';
import { setPageTitle, titleForPath } from '@/utils/pageTitle';
import NavigationBar from './NavigationBar';
import { AttentionPoller } from '@/attention/AttentionPoller';
import ToastLayer from './ToastLayer';
import { appChrome, appChromeStyle } from './appChrome';

export default function AppLayout() {
  const { info } = useAppInfo();
  const capcoEnabled = info?.capco?.enabled;
  const { pathname } = useLocation();

  // Set the browser-tab title from the current route. Pages with dynamic
  // titles (e.g. the model builder) override this in their own effect; that
  // override is replaced when this effect re-runs on the next navigation.
  useEffect(() => {
    setPageTitle(titleForPath(pathname));
  }, [pathname]);

  // Content offset for the fixed navbar + classification banners. The root
  // publishes it as CSS vars so full-height pages fit between them (#2271).
  const chrome = appChrome({ navbar: true, capco: !!capcoEnabled });

  return (
    <div className="min-h-screen" style={appChromeStyle(chrome)}>
      {/* Classification banner - top */}
      <div className="capco-banner">
        <ClassificationBanner
          position="top"
          enabled={!!capcoEnabled}
          backgroundColor={info?.capco?.['background-color']}
          color={info?.capco?.color}
          label={info?.capco?.text}
        />
      </div>

      {/* Navigation bar */}
      <NavigationBar />
      <AttentionPoller />

      {/* Toast notifications (replaces angular-growl), in a body-level layer
          that an open dialog never makes inert (GB8 fix round 1). */}
      <ToastLayer />

      {/* Main content area - adjusts for navbar and banners */}
      <main
        className="w-full overflow-auto"
        style={{
          paddingTop: chrome.top,
          paddingBottom: chrome.bottom,
          minHeight: `calc(100vh - ${chrome.bottom}px)`,
        }}
      >
        <Outlet />
      </main>

      {/* Classification banner - bottom */}
      <div className="capco-banner">
        <ClassificationBanner
          position="bottom"
          enabled={!!capcoEnabled}
          backgroundColor={info?.capco?.['background-color']}
          color={info?.capco?.color}
          label={info?.capco?.text}
        />
      </div>
    </div>
  );
}
